// ============================================================
// NET URL RESOLUTION (Phase 5a Step 1)
// ============================================================
//
// Single source of truth for the server WebSocket URL the client
// should connect to. Resolution priority (highest first):
//
//   1. URL query param  ?net=ws://host:port  or  ?net=wss://host
//   2. localStorage     'puzzle-tactician.netUrl'
//   3. Build-time env   import.meta.env.VITE_DEFAULT_WS_URL
//   4. Hard fallback    ws://localhost:8080
//
// Net mode (whether to use NetworkServerAdapter at all) is enabled
// by either:
//   - Presence of the ?net= query param (PC web flow, Phase 3d)
//   - Running inside a Capacitor native shell (APK flow, Phase 5a)
//   - Running inside an Electron desktop wrapper (PC .exe flow, Phase 5c)
//   - Running as an installed PWA in standalone display-mode (Phase 5d, iOS試玩)
// In native / desktop / installed-PWA bundles there's no way to carry a
// query string, so we default to net mode so the user lands on the
// 4-button menu and can edit the server URL via the in-app prompt.
// Opening the same URL in a regular Safari tab (not standalone) keeps
// the single-player default — only the home-screen icon launch triggers
// net mode auto-on.

const LS_KEY = 'puzzle-tactician.netUrl';

/** Hard fallback used when nothing else resolves. */
export const FALLBACK_NET_URL = 'ws://localhost:8080';

/** Returns true iff the string starts with ws:// or wss://. */
function isValidWsUrl(s: string | null | undefined): s is string {
  if (!s) return false;
  return s.startsWith('ws://') || s.startsWith('wss://');
}

interface QueryNetInfo {
  /** Whether ?net= was present and not '0'/'false'. */
  mode: boolean;
  /** If ?net=ws://... was passed, the explicit URL; else null. */
  explicitUrl: string | null;
}

function readQueryParam(): QueryNetInfo {
  if (typeof window === 'undefined') return { mode: false, explicitUrl: null };
  try {
    const qs = new URLSearchParams(window.location.search);
    const arg = qs.get('net');
    if (!arg || arg === '0' || arg === 'false') return { mode: false, explicitUrl: null };
    if (isValidWsUrl(arg)) return { mode: true, explicitUrl: arg };
    // ?net=1 (or any other non-URL truthy value) — enable mode but no explicit URL
    return { mode: true, explicitUrl: null };
  } catch {
    return { mode: false, explicitUrl: null };
  }
}

/**
 * Returns true when running inside a Capacitor native shell (APK / iOS).
 * Capacitor injects a global `Capacitor` object with `isNativePlatform()`.
 * In plain browsers (Vite dev / preview) this returns false.
 *
 * We probe via globalThis to avoid importing `@capacitor/core` directly
 * — keeps web bundle untouched when Capacitor isn't actually loaded.
 */
function isCapacitorNative(): boolean {
  if (typeof window === 'undefined') return false;
  const cap = (window as any).Capacitor;
  if (!cap) return false;
  if (typeof cap.isNativePlatform === 'function') {
    try { return !!cap.isNativePlatform(); } catch { return false; }
  }
  // Older Capacitor versions: fall back to the boolean field.
  return !!cap.isNative;
}

/**
 * Returns true when running inside an Electron renderer (PC .exe).
 *
 * Electron sets a UA string like
 *   "Mozilla/5.0 ... Electron/31.7.7 ..."
 * which is the most reliable detection path that works under
 * contextIsolation + sandbox (where window.process / require / etc.
 * are all unavailable). Used by Phase 5c .exe build to enable net
 * mode by default — same pattern as Capacitor APK.
 */
function isElectron(): boolean {
  if (typeof navigator === 'undefined') return false;
  return / Electron\//.test(navigator.userAgent);
}

/**
 * Returns true when running as an installed PWA launched from the home
 * screen / Start menu (standalone display mode). Used for iOS Safari
 * "Add to Home Screen" flow — the icon launches without Safari's URL
 * bar, behaving like a native app, so net mode should auto-enable just
 * like Capacitor APK / Electron .exe do.
 *
 * Two detection paths:
 *   - `display-mode: standalone` media query — modern cross-browser
 *     (Chrome, Edge, Android Chrome PWA, recent iOS Safari)
 *   - `navigator.standalone === true` — legacy iOS Safari-specific
 *     (still respected on current iOS, kept as fallback)
 *
 * Opening the page in a regular Safari tab returns false (no auto net
 * mode); only home-screen launch returns true.
 */
function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  // Modern path
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
  } catch { /* fall through */ }
  // Legacy iOS Safari
  try {
    if ((window.navigator as any).standalone === true) return true;
  } catch { /* ignore */ }
  return false;
}

/** True when running inside any "packaged native app" shell (APK / .exe / installed PWA). */
function isPackagedShell(): boolean {
  return isCapacitorNative() || isElectron() || isStandalonePWA();
}

function readBuildEnv(): string | null {
  // import.meta.env is statically replaced by Vite at build time; in tests
  // (node:test) `import.meta.env` is undefined — so we guard defensively.
  try {
    const env = (import.meta as any).env as Record<string, string | undefined> | undefined;
    const v = env?.VITE_DEFAULT_WS_URL;
    return isValidWsUrl(v) ? v : null;
  } catch {
    return null;
  }
}

function readLocalStorage(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const v = localStorage.getItem(LS_KEY);
    return isValidWsUrl(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Returns whether net mode (NetworkServerAdapter path) should be active
 * for this session.
 *
 * Truth table:
 *   ?net=0 / ?net=false             → false (explicit opt-out, even on native)
 *   ?net=1 / ?net=ws://...          → true
 *   no query param, Safari tab      → false (legacy single-player tap-to-start)
 *   no query param, Capacitor APK   → true  (APK default — no URL param available)
 *   no query param, Electron .exe   → true  (.exe default — same reason)
 *   no query param, installed PWA   → true  (home-screen launch — same reason)
 */
export function resolveNetMode(): boolean {
  // Explicit query param wins. readQueryParam() returns mode=false for
  // both "no param" and "?net=0" — disambiguate by re-reading the raw arg.
  if (typeof window !== 'undefined') {
    try {
      const arg = new URLSearchParams(window.location.search).get('net');
      if (arg === '0' || arg === 'false') return false; // explicit opt-out
      if (arg) return true; // any other truthy value
    } catch { /* fall through */ }
  }
  // No query param:
  //   - packaged shells (APK / .exe / installed PWA) → ON (no way to pass ?net).
  //   - production web build with a configured VITE_DEFAULT_WS_URL → ON, so the
  //     deployed site (card-tactician.pages.dev) lands on the 4-button menu with
  //     Create / Join Room reachable (單機對戰 is still one of the four).
  //   - plain dev (`npm run dev`, no env URL) → OFF (single-player default).
  return isPackagedShell() || readBuildEnv() !== null;
}

/**
 * Like `resolveNetMode` but ONLY returns true when the user **explicitly**
 * opted into net mode via `?net=` query param. Packaged-shell auto-enable
 * does NOT count as "explicit".
 *
 * Why this exists: packaged shells (Capacitor APK / Electron / installed
 * PWA) auto-enable net mode so the user lands on the PvP-capable 4-button
 * menu. But that flag is too aggressive for the **single-player → use
 * network adapter as dev override** path in `Game.startBattle`: on Android
 * APK without a reachable WS server, single mode would try a network RPC,
 * sit in CONNECTING state, and the loading overlay would hang on
 * "Initialising battle" forever (Android WebView sometimes never times out
 * on a refused WebSocket connection).
 *
 * Use cases:
 *   - `resolveNetMode()`     → drives the menu UI and PvP code paths
 *                              (enables on packaged shells so PvP buttons
 *                              are reachable).
 *   - `isExplicitNetMode()`  → drives "should single-player ALSO go through
 *                              the network adapter for dev testing?" — only
 *                              true if the dev explicitly typed `?net=...`,
 *                              never just from being inside an APK.
 */
export function isExplicitNetMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const arg = new URLSearchParams(window.location.search).get('net');
    if (arg === '0' || arg === 'false') return false;
    return !!arg;
  } catch {
    return false;
  }
}

/**
 * Returns the resolved WebSocket URL the NetworkServerAdapter should
 * connect to. Always returns a valid ws:// or wss:// URL.
 */
export function resolveNetUrl(): string {
  const q = readQueryParam();
  if (q.explicitUrl) return q.explicitUrl;
  return readLocalStorage() ?? readBuildEnv() ?? FALLBACK_NET_URL;
}

/**
 * Persists a user-edited URL to localStorage so it survives reloads.
 * Throws if the URL is not ws:// or wss://.
 */
export function saveNetUrl(url: string): void {
  const trimmed = url.trim();
  if (!isValidWsUrl(trimmed)) {
    throw new Error('Server URL must start with ws:// or wss://');
  }
  if (typeof localStorage !== 'undefined') {
    try { localStorage.setItem(LS_KEY, trimmed); } catch { /* ignore quota / privacy mode */ }
  }
}

/**
 * Removes the user-saved URL, falling back to build env / fallback on
 * the next resolveNetUrl() call.
 */
export function clearNetUrl(): void {
  if (typeof localStorage !== 'undefined') {
    try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
  }
}
